import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useExternalLeads } from '../../hooks/useExternalLeads';
import { useExternalExchangeRates } from '../../hooks/useExternalExchangeRates';
import { Spinner } from '../../components/common/Spinner';
import { Button } from '../../components/common/Button';
import { ExternalApiError } from '../../services/api/externalAuth';
import { formatDisplayDate } from '../../utils/format';
import { labelTextColor } from '../../utils/labelColor';
import LeadKanbanBoard from '../../components/leads/LeadKanbanBoard';
import RevenueTimeline from '../../components/leads/RevenueTimeline';
import ProjectTimeline from '../../components/leads/ProjectTimeline';
import { BoardListSwitcher } from '../../components/kanban/BoardListSwitcher';
import { convertToCZK } from '../../services/exchangeRate';
import type { Lead, LeadStage, StageTotals } from '../../services/api/leads';
import { weightedValue } from '../../services/api/leadStages';

// Phase 105, Plan 105-10 (D-12) — deliberately distinct from the internal
// page's own sessionStorage key for its label filter
// (pages/sales/LeadsPage.tsx `LABEL_FILTER_KEY`). Internal and external
// pages share the same browser origin, so reusing the internal page's key
// would leak an admin's internal filter selection into the external board
// and back. This file must never write that other key.
const EXTERNAL_LABEL_FILTER_KEY = 'externalLeadLabelFilter';

/**
 * External Leads (Phase 105 SC-2 / SC-3 / D-01 / D-02 / D-06, board added in
 * Plan 105-08 / D-09, CZK conversion + timelines added in Plan 105-09 /
 * D-10 / D-11).
 *
 * Read-only external surface: this page MUST NOT import the internal
 * auth-context module or its hook (v4.0 Landmine #8) — grant/session
 * state is `useExternalAuth`'s job (consumed by `ExternalNavigation`, not
 * here). This page reads leads via `useExternalLeads()` and rates via
 * `useExternalExchangeRates()` — both external-only transports that never
 * import `services/api/client.ts`.
 *
 * Board is the default view (D-09 — Jiri wants board parity with the
 * internal Leads page), rendering `LeadKanbanBoard` with the external
 * audience explicitly set (the seam declared on that component: no drag,
 * no stage mutation, no write affordance in the DOM — see its docblock).
 * The 105-04 read-only table is kept, reachable via the shared
 * `BoardListSwitcher`, as the List view.
 *
 * D-10 (superseding the no-FX decision from Plan 105-08): Plan 105-08
 * declined FX conversion because `services/exchangeRate.ts`'s rate-fetching
 * function routes through `services/api/client.ts`, and using it from
 * `/external/*` would be a Landmine #8 violation. That reasoning about the
 * internal transport was sound, but the conclusion — shipping unconverted,
 * mislabelled deal values — was a correctness bug: `LeadCard.tsx` always
 * formats with `currency: 'CZK'`, so a raw EUR/USD amount rendered stamped
 * with a "Kč" suffix. The fix is a dedicated external rates endpoint (`GET
 * /api/external/exchange-rates`, gated by the same sales-viewer grant) plus
 * `services/api/externalExchangeRate.ts` as its transport. `dealValueInCZK`
 * below mirrors `pages/sales/LeadsPage.tsx`'s computation verbatim in logic
 * (including its "CZK or rates unavailable -> use the raw amount"
 * fallback), and `stageTotals` now emits a single CZK bucket instead of a
 * per-currency map, matching the internal board exactly. `convertToCZK`
 * (imported from `services/exchangeRate.ts`) is safe to reuse here — it is
 * a pure function with no transport dependency, unlike the rate-fetching
 * function in the same module.
 *
 * D-13 (Phase 105, Plan 105-10, superseding Plan 105-08's stale-days prop
 * hardcoded to a literal zero, which disabled the stale marker entirely):
 * Jiri asked for the stale-lead marker on 2026-08-31 after comparing this
 * board to the internal one — the earlier "internal pipeline-management
 * signal, not for a client" rationale was overruled, and was weak anyway:
 * staleness is derived from `stageChangedAt`, which is already in the
 * external payload, so a determined viewer could compute it regardless.
 * `GET /admin/settings` (the source of `lead_staleness_days`) is
 * admin-only, so this page cannot read the threshold directly — the backend
 * now carries the resolved value as `leadStalenessDays` on the
 * `/api/external/leads` list response (`externalLeadService.ts`), and that
 * value (not a hardcoded guess) is passed to `LeadKanbanBoard`'s
 * `staleDays` prop, unchanged from the shipped component.
 *
 * D-12 (Phase 105, Plan 105-10): label filter chips. The internal page
 * derives its chip set from a dedicated internal label-fetching hook
 * backed by `GET /api/leads/labels`, which is internal-gated. Rather than
 * add an external twin of that route, the chip set here is derived from
 * the `labels` already present on each fetched lead — deduped by id and
 * sorted by name so the chip row is stable across fetches. Filtering is OR
 * semantics and purely client-side over already-fetched data (no mutation,
 * no internal label-fetching hook, no external labels endpoint). The
 * selection persists to `sessionStorage` under
 * `EXTERNAL_LABEL_FILTER_KEY` ('externalLeadLabelFilter') — see that
 * constant's comment for why it must differ from the internal page's key.
 *
 * D-11 (superseding Plan 105-08's exclusion): `RevenueTimeline` and
 * `ProjectTimeline` were previously excluded as "mutation-oriented" —
 * that was wrong. Both are pure Recharts presentation (their only imports
 * are `react`, `recharts`, the `Lead` type, and `utils/format`; neither
 * calls a mutation hook, the internal API client, or any auth context) and
 * are mounted here unmodified, exactly as the internal page mounts them. No
 * `audience` prop is needed on either — they were already audience-agnostic.
 *
 * Deliberately NOT reused: the internal board's create/edit form modal,
 * which is genuinely mutation-oriented. No pagination: `GET
 * /api/external/leads` is unpaginated by design (Plan 105-02) and
 * production holds ~30 leads.
 */
export function LeadsPage() {
  const leadsQuery = useExternalLeads();
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState<'board' | 'list'>('board');

  // D-10: called unconditionally (rules of hooks) — before any early return
  // below — so a rates-query failure never blocks the leads gates that
  // follow. `today` mirrors the internal page's own `YYYY-MM-DD` derivation.
  const today = new Date().toISOString().split('T')[0]!;
  const { data: exchangeRates } = useExternalExchangeRates(today);

  // (1) Loading gate — only while no data has ever landed, matching
  // ExternalUsersPage's `isLoading && !data` gate order.
  if (leadsQuery.isLoading && !leadsQuery.data) {
    return (
      <div className="flex items-center justify-center py-24">
        <Spinner />
      </div>
    );
  }

  const err = leadsQuery.error as ExternalApiError | null;

  // (2) 403 (revoked/never-held grant) — an explicit access-denied panel,
  // returned BEFORE any row-rendering path so a stale cached `data` object
  // (T-105-CACHE) can never render alongside the error.
  if (err instanceof ExternalApiError && err.statusCode === 403) {
    return (
      <div className="text-center py-12">
        <h1 className="text-lg font-semibold text-gray-900">Access no longer available</h1>
        <p className="mt-2 text-sm text-gray-500">
          Lead access is no longer available for your account. Contact the person who invited
          you if you believe this is a mistake.
        </p>
      </div>
    );
  }

  // (3) Any other error — generic message + retry.
  if (err) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-700">Couldn&apos;t load leads — try refreshing.</p>
        <Button
          variant="secondary"
          size="sm"
          className="mt-3"
          onClick={() => leadsQuery.refetch()}
        >
          Retry
        </Button>
      </div>
    );
  }

  const leads = leadsQuery.data?.data ?? [];
  const total = leadsQuery.data?.total ?? 0;
  // D-13: fall back to 14 when the field is absent (e.g. a stale cached
  // response shape) — the canonical default lives server-side in
  // `SETTING_DEFAULTS` (appSettingsService.ts); this is not a re-guess, it's
  // the same default the server itself would have applied.
  const leadStalenessDays = leadsQuery.data?.leadStalenessDays ?? 14;
  // MKS-345 — the pipeline the admin configured, carried on the same response.
  const stages = leadsQuery.data?.stages ?? [];

  return (
    <LeadsPageContent
      leads={leads}
      total={total}
      stages={stages}
      viewMode={viewMode}
      onViewModeChange={setViewMode}
      navigate={navigate}
      exchangeRates={exchangeRates}
      leadStalenessDays={leadStalenessDays}
    />
  );
}

function LeadsPageContent({
  leads,
  total,
  stages,
  viewMode,
  onViewModeChange,
  navigate,
  exchangeRates,
  leadStalenessDays,
}: {
  leads: Lead[];
  total: number;
  stages: LeadStage[];
  viewMode: 'board' | 'list';
  onViewModeChange: (next: 'board' | 'list') => void;
  navigate: ReturnType<typeof useNavigate>;
  exchangeRates: Map<string, number> | undefined;
  leadStalenessDays: number;
}) {
  // D-12: derive the chip set from the fetched leads — dedupe by id, sort
  // by name so the row is stable across fetches (a Map/Set built by lead
  // traversal is otherwise data-order-dependent).
  const allLabels = useMemo(() => {
    const byId = new Map<number, Lead['labels'][number]>();
    for (const lead of leads) {
      for (const label of lead.labels) {
        if (!byId.has(label.id)) byId.set(label.id, label);
      }
    }
    return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [leads]);

  // D-12: persisted under a distinct key from the internal page's own
  // filter key — see EXTERNAL_LABEL_FILTER_KEY's comment above.
  const [selectedLabelIds, setSelectedLabelIds] = useState<number[]>(() => {
    try {
      const raw = sessionStorage.getItem(EXTERNAL_LABEL_FILTER_KEY);
      return raw ? (JSON.parse(raw) as number[]) : [];
    } catch {
      return [];
    }
  });
  useEffect(() => {
    try {
      sessionStorage.setItem(EXTERNAL_LABEL_FILTER_KEY, JSON.stringify(selectedLabelIds));
    } catch {
      // sessionStorage unavailable — filter simply won't persist
    }
  }, [selectedLabelIds]);

  // OR semantics: a lead matches if it carries at least one selected label.
  // Mirrors pages/sales/LeadsPage.tsx lines 96-99.
  const filteredLeads = useMemo(() => {
    if (selectedLabelIds.length === 0) return leads;
    return leads.filter((l) => l.labels.some((lab) => selectedLabelIds.includes(lab.id)));
  }, [leads, selectedLabelIds]);

  // D-10: mirrors pages/sales/LeadsPage.tsx lines 101-114 verbatim in logic —
  // CZK (or rates unavailable, T-109-FXFAIL) uses the raw amount; every
  // other currency is converted via convertToCZK with the ČNB rate (default
  // 1 if the currency is missing from the rates map). Keyed off all `leads`
  // (not `filteredLeads`) — it's a lookup by id, so filtering it is
  // unnecessary, exactly as the internal page does.
  const dealValueInCZK = useMemo(() => {
    const map = new Map<number, number>();
    for (const lead of leads) {
      const raw = parseFloat(lead.dealValue ?? '0') || 0;
      const currency = lead.dealValueCurrency ?? 'CZK';
      if (currency === 'CZK' || !exchangeRates) {
        map.set(lead.id, raw);
      } else {
        const rate = exchangeRates.get(currency) ?? 1;
        map.set(lead.id, convertToCZK(raw, currency, rate));
      }
    }
    return map;
  }, [leads, exchangeRates]);

  // Single CZK bucket per column — mirrors pages/sales/LeadsPage.tsx lines
  // 116-129. Totals reflect the filtered view (D-12) so column counts match
  // visible cards, mirroring internal line 121.
  const stageTotals = useMemo<StageTotals>(() => {
    const totals: StageTotals = {};
    for (const stage of stages) {
      const stageLeads = filteredLeads.filter((l) => l.stageId === stage.id);
      const total = stageLeads.reduce((sum, l) => sum + (dealValueInCZK.get(l.id) ?? 0), 0);
      const value: Record<string, number> = total ? { CZK: total } : {};
      totals[String(stage.id)] = {
        count: stageLeads.length,
        value,
        weightedValue: weightedValue(value, stage.probability),
      };
    }
    return totals;
  }, [filteredLeads, dealValueInCZK, stages]);

  const handleCardClick = (lead: Lead) => {
    navigate(`/external/leads/${lead.id}`);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Leads</h1>
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-500">{total} leads</span>
          <BoardListSwitcher value={viewMode} onChange={onViewModeChange} />
        </div>
      </div>

      {/* D-12: label filter chips, mirroring pages/sales/LeadsPage.tsx's
          markup and active/inactive styling. Filter toggles only — no
          label create/edit/delete affordance. */}
      {allLabels.length > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium text-gray-500">Filter by label:</span>
          {allLabels.map((label) => {
            const active = selectedLabelIds.includes(label.id);
            return (
              <button
                key={label.id}
                type="button"
                onClick={() =>
                  setSelectedLabelIds((prev) =>
                    active ? prev.filter((id) => id !== label.id) : [...prev, label.id]
                  )
                }
                style={
                  active
                    ? { backgroundColor: label.color, color: labelTextColor(label.color), borderColor: label.color }
                    : { borderColor: label.color, color: label.color }
                }
                className="rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors"
              >
                {label.name}
              </button>
            );
          })}
          {selectedLabelIds.length > 0 && (
            <button
              type="button"
              onClick={() => setSelectedLabelIds([])}
              className="text-xs text-gray-400 underline hover:text-gray-600"
            >
              Clear
            </button>
          )}
        </div>
      )}

      {/* Empty gate — keyed off the full unfiltered dataset, matching the
          internal page: a label filter that narrows to zero leads shows
          empty board columns / an empty table, not this "no leads at all"
          message. */}
      {leads.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-500">No leads yet.</p>
        </div>
      ) : viewMode === 'board' ? (
        <>
          <LeadKanbanBoard
            leads={filteredLeads}
            stages={stages}
            stageTotals={stageTotals}
            staleDays={leadStalenessDays}
            dealValueCZKMap={dealValueInCZK}
            onCardClick={handleCardClick}
            audience="external"
          />
          <RevenueTimeline leads={filteredLeads} exchangeRates={exchangeRates} onLeadClick={handleCardClick} />
          <ProjectTimeline leads={filteredLeads} onLeadClick={handleCardClick} />
        </>
      ) : (
        <div className="overflow-auto rounded-lg border border-gray-200 bg-white">
          <table className="divide-y divide-gray-200 w-full">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Name
                </th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Customer
                </th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Stage
                </th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Deal value
                </th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Expected close
                </th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Owner
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredLeads.map((lead) => (
                <tr
                  key={lead.id}
                  className="cursor-pointer hover:bg-gray-50"
                  onClick={() => navigate(`/external/leads/${lead.id}`)}
                >
                  <td className="px-4 py-2 text-sm font-medium text-gray-900">
                    <Link
                      to={`/external/leads/${lead.id}`}
                      className="hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {lead.name}
                    </Link>
                    {lead.labels.length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {lead.labels.map((l) => (
                          <span
                            key={l.id}
                            style={{ backgroundColor: l.color, color: labelTextColor(l.color) }}
                            className="inline-block rounded px-1.5 py-0.5 text-[10px] font-medium leading-tight"
                          >
                            {l.name}
                          </span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-2 text-sm text-gray-500">{lead.customer?.name ?? '—'}</td>
                  <td className="px-4 py-2 text-sm">
                    <span className="inline-block rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-700">
                      {lead.stageName}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-sm text-gray-500">
                    {lead.dealValue ? `${lead.dealValue} ${lead.dealValueCurrency}` : '—'}
                  </td>
                  <td className="px-4 py-2 text-sm text-gray-500">
                    {formatDisplayDate(lead.expectedCloseDate)}
                  </td>
                  <td className="px-4 py-2 text-sm text-gray-500">
                    {lead.owner.firstName} {lead.owner.lastName}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
